import { useRef, useEffect, useCallback } from 'react';
import type { PoseFrame } from '@/lib/realtime-pose';
import { SKELETON_CONNECTIONS, LANDMARK_INDEX } from '@/lib/realtime-pose';

interface PoseOverlayProps {
  /** Current pose frame from realtime detection (null = no pose) */
  frame: PoseFrame | null;
  /** Native video resolution width */
  videoWidth: number;
  /** Native video resolution height */
  videoHeight: number;
  /** Whether the model is still loading */
  modelLoading: boolean;
  /** Countdown value (3, 2, 1, 0). 0 = not counting. */
  countdown: number;
  /** Self-timer countdown (0 = not active). Takes visual priority over pose countdown. */
  selfTimerCountdown: number;
  /** Whether voice guide is active (shows small speaker icon) */
  voiceGuideActive?: boolean;
  /** Capture type — affects alignment badge display */
  captureType?: 'front' | 'side';
}

/**
 * Canvas overlay that draws a live skeleton, alignment feedback,
 * and auto-capture countdown on top of the camera feed.
 *
 * Must be absolutely positioned over the video element with the same
 * dimensions. Uses object-fit: cover math to map normalized landmark
 * coords to the visible viewport area.
 */
export function PoseOverlay({
  frame,
  videoWidth,
  videoHeight,
  modelLoading,
  countdown,
  selfTimerCountdown,
  voiceGuideActive = false,
  captureType = 'front',
}: PoseOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /** Map normalized landmark coords to canvas pixel coords (object-fit: cover). */
  const toCanvas = useCallback(
    (
      normX: number,
      normY: number,
      cw: number,
      ch: number
    ): [number, number] => {
      if (videoWidth === 0 || videoHeight === 0) return [0, 0];

      const videoAspect = videoWidth / videoHeight;
      const canvasAspect = cw / ch;

      let scale: number, offsetX: number, offsetY: number;

      if (videoAspect > canvasAspect) {
        // Video wider than canvas — cropped left/right
        scale = ch / videoHeight;
        offsetX = (videoWidth * scale - cw) / 2;
        offsetY = 0;
      } else {
        // Video taller than canvas — cropped top/bottom
        scale = cw / videoWidth;
        offsetX = 0;
        offsetY = (videoHeight * scale - ch) / 2;
      }

      const px = normX * videoWidth * scale - offsetX;
      const py = normY * videoHeight * scale - offsetY;
      return [px, py];
    },
    [videoWidth, videoHeight]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match canvas pixel size to its CSS display size
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2); // cap at 2x for perf
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const cw = rect.width;
    const ch = rect.height;

    ctx.clearRect(0, 0, cw, ch);

    // ── Self-timer countdown (takes priority over everything) ──
    if (selfTimerCountdown > 0) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(0, 0, cw, ch);

      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      const fontSize = Math.min(cw * 0.4, 160);
      ctx.font = `bold ${fontSize}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(255, 180, 0, 0.6)';
      ctx.shadowBlur = 40;
      ctx.fillText(String(selfTimerCountdown), cw / 2, ch * 0.42);
      ctx.restore();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = 'bold 18px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Get ready...', cw / 2, ch * 0.56);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.font = '13px -apple-system, sans-serif';
      ctx.fillText('Tap Cancel below to stop', cw / 2, ch * 0.62);
      return;
    }

    // ── No pose detected — show static fallback or loading ──
    if (!frame) {
      if (modelLoading) {
        drawLoadingIndicator(ctx, cw, ch);
      } else {
        drawStaticGuide(ctx, cw, ch);
      }
      drawAlignmentHUD(ctx, cw, ch, null, countdown, captureType);
      return;
    }

    const { landmarks, alignment } = frame;

    // ── Draw skeleton connections ──
    for (const [from, to] of SKELETON_CONNECTIONS) {
      const lmFrom = landmarks[from];
      const lmTo = landmarks[to];
      if (!lmFrom || !lmTo) continue;

      const minVis = Math.min(lmFrom.visibility, lmTo.visibility);
      if (minVis < 0.3) continue;

      const [x1, y1] = toCanvas(lmFrom.x, lmFrom.y, cw, ch);
      const [x2, y2] = toCanvas(lmTo.x, lmTo.y, cw, ch);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineWidth = 3;
      ctx.strokeStyle =
        minVis > 0.7
          ? 'rgba(0, 255, 170, 0.8)'
          : minVis > 0.5
            ? 'rgba(255, 220, 50, 0.7)'
            : 'rgba(255, 80, 80, 0.5)';
      ctx.stroke();
    }

    // ── Draw keypoint dots ──
    const keyIndices = Object.values(LANDMARK_INDEX);
    for (const idx of keyIndices) {
      const lm = landmarks[idx];
      if (!lm || lm.visibility < 0.3) continue;

      const [x, y] = toCanvas(lm.x, lm.y, cw, ch);
      const radius = lm.visibility > 0.7 ? 5 : 3;

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle =
        lm.visibility > 0.7
          ? 'rgba(0, 255, 170, 0.9)'
          : lm.visibility > 0.5
            ? 'rgba(255, 220, 50, 0.8)'
            : 'rgba(255, 80, 80, 0.6)';
      ctx.fill();
    }

    // ── Draw alignment HUD ──
    drawAlignmentHUD(ctx, cw, ch, alignment, countdown, captureType);

    // ── Voice guide indicator ──
    if (voiceGuideActive) {
      ctx.save();
      ctx.font = `${Math.round(cw * 0.04)}px sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      ctx.fillText('\uD83D\uDD0A', cw - 12, 12);
      ctx.restore();
    }
  }, [frame, videoWidth, videoHeight, modelLoading, countdown, selfTimerCountdown, voiceGuideActive, toCanvas]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 z-[2]"
      style={{ width: '100%', height: '100%' }}
    />
  );
}

// ─── Drawing helpers ────────────────────────────────────────────

function drawStaticGuide(ctx: CanvasRenderingContext2D, cw: number, ch: number) {
  ctx.save();
  const cx = cw / 2;
  const scale = Math.min(cw * 0.35, 130);
  const top = ch * 0.12;

  ctx.setLineDash([8, 5]);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 2;

  // Head
  ctx.beginPath();
  ctx.ellipse(cx, top + scale * 0.18, scale * 0.22, scale * 0.28, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Torso
  ctx.beginPath();
  ctx.moveTo(cx - scale * 0.4, top + scale * 0.52);
  ctx.lineTo(cx - scale * 0.35, top + scale * 1.6);
  ctx.lineTo(cx + scale * 0.35, top + scale * 1.6);
  ctx.lineTo(cx + scale * 0.4, top + scale * 0.52);
  ctx.closePath();
  ctx.stroke();

  // Arms
  ctx.beginPath();
  ctx.moveTo(cx - scale * 0.4, top + scale * 0.55);
  ctx.lineTo(cx - scale * 0.65, top + scale * 1.2);
  ctx.moveTo(cx + scale * 0.4, top + scale * 0.55);
  ctx.lineTo(cx + scale * 0.65, top + scale * 1.2);
  ctx.stroke();

  // Legs
  ctx.beginPath();
  ctx.moveTo(cx - scale * 0.2, top + scale * 1.6);
  ctx.lineTo(cx - scale * 0.25, top + scale * 2.8);
  ctx.moveTo(cx + scale * 0.2, top + scale * 1.6);
  ctx.lineTo(cx + scale * 0.25, top + scale * 2.8);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.restore();
}

function drawLoadingIndicator(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number
) {
  drawStaticGuide(ctx, cw, ch);

  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
  ctx.fillRect(cw / 2 - 90, ch * 0.48, 180, 36);

  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.font = '13px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Loading pose detector...', cw / 2, ch * 0.48 + 23);
  ctx.restore();
}

interface AlignmentInfo {
  head: boolean;
  shoulders: boolean;
  torso: boolean;
  arms: boolean;
  legs: boolean;
  distance: string;
  allAligned: boolean;
  overallScore: number;
}

function drawAlignmentHUD(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  alignment: AlignmentInfo | null,
  countdown: number,
  captureType: 'front' | 'side' = 'front'
) {
  // ── Countdown overlay ──
  if (countdown > 0) {
    // Dim background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, cw, ch);

    // Large countdown number
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.font = `bold ${Math.min(cw * 0.35, 140)}px -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 200, 120, 0.5)';
    ctx.shadowBlur = 30;
    ctx.fillText(String(countdown), cw / 2, ch * 0.45);
    ctx.restore();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '16px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Hold still...', cw / 2, ch * 0.58);
    return;
  }

  if (!alignment) return;

  // ── Body zone badges (left side) ──
  // For side photos, arms aren't required so don't show that badge
  const zones = captureType === 'side'
    ? [
        { label: 'Head', ok: alignment.head },
        { label: 'Shoulder', ok: alignment.shoulders },
        { label: 'Torso', ok: alignment.torso },
        { label: 'Legs', ok: alignment.legs },
      ]
    : [
        { label: 'Head', ok: alignment.head },
        { label: 'Shoulders', ok: alignment.shoulders },
        { label: 'Torso', ok: alignment.torso },
        { label: 'Arms', ok: alignment.arms },
        { label: 'Legs', ok: alignment.legs },
      ];

  const badgeX = 14;
  let badgeY = ch * 0.3;
  const lineH = 28;

  ctx.save();
  ctx.font = '12px -apple-system, sans-serif';
  ctx.textAlign = 'left';

  for (const zone of zones) {
    const icon = zone.ok ? '\u2713' : '\u2717'; // ✓ or ✗
    const color = zone.ok ? 'rgba(0, 230, 120, 0.9)' : 'rgba(255, 255, 255, 0.45)';

    // Badge background
    ctx.fillStyle = zone.ok ? 'rgba(0, 180, 100, 0.25)' : 'rgba(0, 0, 0, 0.3)';
    const textW = ctx.measureText(`${icon} ${zone.label}`).width + 16;
    ctx.beginPath();
    ctx.roundRect(badgeX, badgeY - 8, textW, 22, 6);
    ctx.fill();

    // Badge text
    ctx.fillStyle = color;
    ctx.fillText(`${icon} ${zone.label}`, badgeX + 8, badgeY + 7);
    badgeY += lineH;
  }

  // ── Distance indicator (bottom center) ──
  if (alignment.distance !== 'unknown') {
    const distMsg =
      alignment.distance === 'too_close'
        ? 'Step back'
        : alignment.distance === 'too_far'
          ? 'Step closer'
          : '';

    if (distMsg) {
      const msgW = ctx.measureText(distMsg).width + 28;
      ctx.fillStyle = 'rgba(255, 170, 50, 0.85)';
      ctx.beginPath();
      ctx.roundRect(cw / 2 - msgW / 2, ch * 0.88, msgW, 28, 14);
      ctx.fill();

      ctx.fillStyle = '#000';
      ctx.font = 'bold 13px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(distMsg, cw / 2, ch * 0.88 + 19);
    }
  }

  // ── Overall readiness bar (top right) ──
  const barW = 60;
  const barH = 6;
  const barX = cw - barW - 14;
  const barY = ch * 0.3;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
  ctx.beginPath();
  ctx.roundRect(barX, barY, barW, barH, 3);
  ctx.fill();

  const fillColor = alignment.overallScore > 0.8 ? '#00e676' : alignment.overallScore > 0.5 ? '#ffd740' : '#ff5252';
  ctx.fillStyle = fillColor;
  ctx.beginPath();
  ctx.roundRect(barX, barY, barW * alignment.overallScore, barH, 3);
  ctx.fill();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.font = '10px -apple-system, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(`${Math.round(alignment.overallScore * 100)}%`, cw - 14, barY + barH + 14);

  ctx.restore();
}
